/**
 * MarketingBisonTaxiCommander · v2.6.9 batch CI
 * =============================================
 * Public marketing page at /bison-taxi-commander. Listed in the public
 * About dropdown nav alongside Leadership + Drivers.
 *
 * Structure (per spec):
 *   1. Branded header section (TAXICOMMANDER logo + title + lead text)
 *   2. Information content block (rich-text body or fallback copy)
 *   3. Live "Online Status" widget — same module as Admin → Developer License
 *
 * Admins control the long-form copy via cfg.bison_taxi_commander.body
 * (rich HTML rendered through dangerouslySetInnerHTML — admin-authored only).
 */
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Radio, Cpu, Eye, Satellite } from "lucide-react";
import BisonStatusWidget from "@/components/BisonStatusWidget";
import useSiteConfig from "@/hooks/useSiteConfig";

const DEFAULT_BODY_HTML = `
<p>
  <strong>Bison Taxi Commander</strong> is the live dispatch + monitoring platform
  that powers every Buller Taxi ride. Built by BISON AI &amp; Technology, the
  Commander stack ties together our fleet GPS feed, automated quote engine,
  payment processing, and on-call dispatcher console into a single
  always-on operations cockpit.
</p>
<h2>What it does</h2>
<ul>
  <li>Real-time fleet GPS tracking across all 77 counties of Oklahoma</li>
  <li>Multi-tier metered pricing with transparent rider-side quotes</li>
  <li>Square-hardware payment processing (drivers) + in-app card processing (riders)</li>
  <li>Live "Online Status" health checks every 15 seconds for the data center, GPS, AI engine, and video/audio monitoring</li>
  <li>OSBI fingerprint background-check enforcement before any driver activation</li>
</ul>
<h2>Why it matters</h2>
<p>
  Every trip routed through Bison Taxi Commander is geofence-validated, distance-metered
  and time-stamped — so the price you see at the curb is the price you pay at the door.
  Our dispatchers see your vehicle on a live map the entire time, and the system pages
  on-call staff the moment a driver crosses a service boundary or a panic alert fires.
</p>
`;

export default function MarketingBisonTaxiCommander() {
  const { cfg } = useSiteConfig();
  const commanderLogo = cfg?.global?.taxi_commander_logo_url;
  const bodyHtml = cfg?.bison_taxi_commander?.body || DEFAULT_BODY_HTML;
  const heading = cfg?.bison_taxi_commander?.title || "Bison Taxi Commander";
  const lead = cfg?.bison_taxi_commander?.lead || "The live dispatch + monitoring platform behind every Buller Taxi ride.";
  // v2.8 · CMS-managed "Our Fleet Technology Partners" banner.
  const btc = cfg?.bison_taxi_commander || {};
  const partnersUrl = btc.partners_banner_url || "https://customer-assets-7cd3h4nn.emergentagent.net/job_taxi-meter-app-5/artifacts/8mu0yngi_fleet-technology-partners-transparent.webp";
  const partnersLink = btc.partners_banner_link || "";
  const partnersEnabled = btc.partners_banner_enabled !== false;

  return (
    <MarketingLayout>
      {/* 1 · Branded header */}
      <section className="relative border-b border-amber-400 overflow-hidden bg-zinc-950" data-testid="btc-hero">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(250,204,21,0.6) 0%, transparent 35%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.5) 0%, transparent 40%)",
        }}/>
        <div className="relative max-w-5xl mx-auto px-5 py-14 sm:py-20 text-center">
          {commanderLogo && (
            <img
              src={commanderLogo}
              alt="TAXIcab Commander"
              className="mx-auto h-24 sm:h-32 object-contain drop-shadow-[0_4px_18px_rgba(250,204,21,0.35)]"
              data-testid="btc-hero-logo"
            />
          )}
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold mt-4">POWERED BY BISON AI &amp; TECHNOLOGY</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-zinc-100 mt-2" data-testid="btc-title">{heading}</h1>
          <p className="text-zinc-300 mt-4 max-w-2xl mx-auto leading-relaxed text-base" data-testid="btc-lead">{lead}</p>
        </div>
      </section>

      {/* 2 · Information content + 3 · Live Status widget side-by-side on desktop */}
      <section className="max-w-6xl mx-auto px-5 py-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8" data-testid="btc-body-row">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold mb-3">
            <Cpu className="h-3.5 w-3.5"/>About the Platform
          </div>
          <div
            className="prose prose-invert max-w-none text-zinc-300 leading-relaxed text-base"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
            data-testid="btc-body-html"
          />
          <FeaturePills/>
        </div>

        {/* Right column — Online Status widget cloned from Developer License */}
        <aside data-testid="btc-status-column">
          <div className="lg:sticky lg:top-6 space-y-4">
            <BisonStatusWidget testid="btc-status-widget"/>
            <Card className="bg-zinc-900/70 border-amber-400/20 p-4" data-testid="btc-licensee-card">
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-2">Licensed To</div>
              <div className="space-y-1 text-xs text-zinc-300">
                <div className="flex justify-between"><span className="text-zinc-500">Company</span><span className="font-semibold">Buller Taxi Services, LLC</span></div>
                {/* v2.6.9 batch CJ · Serial # row removed per spec — public
                    page should not expose the BAI license serial. */}
                <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className="text-emerald-400 font-bold uppercase tracking-wider text-[11px]">Current</span></div>
              </div>
              <div className="mt-3 pt-2 border-t border-zinc-800 flex items-center justify-between text-[10px] text-zinc-500">
                <span>BISON AI &amp; Technology</span>
                <ShieldCheck className="h-3.5 w-3.5 text-amber-400/60"/>
              </div>
            </Card>
          </div>
        </aside>
      </section>

      {/* v2.8 · "Our Fleet Technology Partners" banner — full-width section,
          centered to the PAGE below the About-the-Platform section.
          CMS-managed image + optional external link. */}
      {partnersEnabled && partnersUrl && (
        <section className="max-w-6xl mx-auto px-5 pb-14 flex justify-center" data-testid="btc-partners-banner">
          {partnersLink ? (
            <a
              href={partnersLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full max-w-3xl transition-transform hover:scale-[1.01]"
              data-testid="btc-partners-banner-link"
            >
              <img src={partnersUrl} alt="Our Fleet Technology Partners" loading="lazy" decoding="async" className="w-full h-auto"/>
            </a>
          ) : (
            <img src={partnersUrl} alt="Our Fleet Technology Partners" loading="lazy" decoding="async" className="w-full max-w-3xl h-auto" data-testid="btc-partners-banner-img"/>
          )}
        </section>
      )}
    </MarketingLayout>
  );
}

function FeaturePills() {
  const items = [
    { icon: Satellite, label: "Live GPS" },
    { icon: Radio,     label: "24/7 Dispatch" },
    { icon: Cpu,       label: "AI Routing" },
    { icon: Eye,       label: "Video / Audio Monitoring" },
    { icon: ShieldCheck, label: "OSBI Background Checks" },
  ];
  return (
    <div className="mt-8 flex flex-wrap gap-2" data-testid="btc-feature-pills">
      {items.map(({ icon: Ic, label }) => (
        <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-[10px] uppercase tracking-wider text-amber-200 font-bold">
          <Ic className="h-3 w-3"/>{label}
        </span>
      ))}
    </div>
  );
}
